import { X } from 'lucide-react';
import React, { useMemo } from 'react';
import { cn } from 'src/tef/components/utils/cn';
import { forwardProps } from 'src/tef/components/utils/forwardProps';
import type { ComponentSize, ForwardedHTMLProps } from 'src/tef/components/utils/types';

type BadgeType = 'success' | 'warning' | 'error' | 'info' | 'neutral' | 'primary';
type BadgeVariant = 'filled' | 'outlined' | 'soft';

interface StatusBadgeProps extends ForwardedHTMLProps {
  children: React.ReactNode;
  type?: BadgeType;
  variant?: BadgeVariant;
  size?: ComponentSize;
  icon?: React.ReactNode;
  dot?: boolean;
  pulse?: boolean;
  removable?: boolean;
  onRemove?: () => void;
  className?: string;
}

const typeStyles: Record<BadgeType, { soft: string; filled: string; outlined: string; dot: string }> = {
  success: {
    soft: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400',
    filled: 'bg-emerald-500 text-white',
    outlined: 'border-emerald-500 text-emerald-600 dark:text-emerald-400',
    dot: 'bg-emerald-500',
  },
  warning: {
    soft: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
    filled: 'bg-amber-500 text-white',
    outlined: 'border-amber-500 text-amber-600 dark:text-amber-400',
    dot: 'bg-amber-500',
  },
  error: {
    soft: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
    filled: 'bg-red-500 text-white',
    outlined: 'border-red-500 text-red-600 dark:text-red-400',
    dot: 'bg-red-500',
  },
  info: {
    soft: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
    filled: 'bg-blue-500 text-white',
    outlined: 'border-blue-500 text-blue-600 dark:text-blue-400',
    dot: 'bg-blue-500',
  },
  neutral: {
    soft: 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300',
    filled: 'bg-slate-500 text-white',
    outlined: 'border-slate-400 dark:border-slate-500 text-slate-600 dark:text-slate-400',
    dot: 'bg-slate-400',
  },
  primary: {
    soft: 'bg-primary/10 text-primary',
    filled: 'bg-primary text-white',
    outlined: 'border-primary text-primary',
    dot: 'bg-primary',
  },
};

const sizeConfig: Record<ComponentSize, { badge: string; icon: string; dot: string }> = {
  xs: { badge: 'px-1.5 py-0.5 text-[10px] gap-1', icon: 'w-2.5 h-2.5', dot: 'w-1.5 h-1.5' },
  sm: { badge: 'px-2 py-0.5 text-xs gap-1', icon: 'w-3 h-3', dot: 'w-1.5 h-1.5' },
  md: { badge: 'px-2.5 py-1 text-xs gap-1.5', icon: 'w-3.5 h-3.5', dot: 'w-2 h-2' },
  lg: { badge: 'px-3 py-1 text-sm gap-1.5', icon: 'w-4 h-4', dot: 'w-2 h-2' },
  xl: { badge: 'px-3.5 py-1.5 text-sm gap-2', icon: 'w-4 h-4', dot: 'w-2.5 h-2.5' },
};

export const StatusBadge = React.forwardRef<HTMLSpanElement, StatusBadgeProps>(
  (
    {
      children,
      type = 'neutral',
      variant = 'soft',
      size = 'md',
      icon,
      dot = false,
      pulse = false,
      removable = false,
      onRemove,
      className,
      ...props
    },
    ref
  ) => {
    const forwardedProps = forwardProps(props);

    const styles = typeStyles[type];
    const sizeStyles = sizeConfig[size];

    const badgeClasses = useMemo(() => {
      return cn(
        'inline-flex items-center font-medium rounded-full',
        sizeStyles.badge,
        styles[variant],
        variant === 'outlined' && 'border bg-transparent',
        className
      );
    }, [variant, styles, sizeStyles, className]);

    return (
      <span ref={ref} className={badgeClasses} {...forwardedProps} {...props}>
        {dot && (
          <span className="relative flex-shrink-0">
            <span className={cn(sizeStyles.dot, 'rounded-full', styles.dot)} />
            {pulse && (
              <span
                className={cn(sizeStyles.dot, 'rounded-full absolute inset-0 animate-ping opacity-75', styles.dot)}
              />
            )}
          </span>
        )}
        {icon && !dot && (
          <span className={cn(sizeStyles.icon, 'flex-shrink-0')} aria-hidden="true">
            {icon}
          </span>
        )}
        <span className="inline-flex items-center gap-1">{children}</span>
        {removable && (
          <button
            type="button"
            onClick={onRemove}
            className={cn(
              'flex-shrink-0 -mr-1 ml-0.5 rounded-full p-0.5',
              'hover:bg-black/10 dark:hover:bg-white/10',
              'focus:outline-none focus:ring-1 focus:ring-current',
              'transition-colors'
            )}
            aria-label="Remove"
          >
            <X className={sizeStyles.icon} />
          </button>
        )}
      </span>
    );
  }
);

StatusBadge.displayName = 'StatusBadge';

export default StatusBadge;
